import * as React from "react";
import { useState } from "react";
import PropTypes from "prop-types";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  OutlinedInput,
  FormControl,
  FormLabel,
  Box,
  IconButton
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close"; // Make sure to install @mui/icons-material
import { useDispatch } from "react-redux";
import { showToast } from "../../store/slices/toastSlice";
import axiosInstance from "../../services/core/axiosInstance";
import { forgotPassword } from "../../services/api/auth";

function ForgotPassword({ open, handleClose }) {
  const dispatch = useDispatch();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!email) return;

    setLoading(true);
    try {
      const response = await forgotPassword(email);
      dispatch(showToast({ message: response?.message, severity: "success", }));
      setEmail("");
      handleClose();
    } catch (error) {
      dispatch(
        showToast({
          message: error.response?.message || error.message || "Failed to send reset link",
          severity: "error",
        })
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        component: "form",
        onSubmit: handleSubmit,
        sx: {
          borderRadius: 3,
          p: 2,
          boxShadow: "0px 10px 30px rgba(0, 0, 0, 0.08)",
        }
      }}
    >
      {/* Upper Right Close Button */}
      <IconButton
        aria-label="close"
        onClick={handleClose}
        sx={{
          position: "absolute",
          right: 16,
          top: 16,
          color: "grey.500",
        }}
      >
        <CloseIcon />
      </IconButton>

      <DialogTitle
        sx={{
          fontSize: "1.5rem",
          fontWeight: 700,
          pb: 1,
          pt: 1
        }}
      >
        Reset Password
      </DialogTitle>

      <DialogContent sx={{ pb: 1 }}>
        <DialogContentText
          sx={{
            fontSize: "0.875rem",
            color: "text.secondary",
            mb: 3
          }}
        >
          Enter your account&apos;s email address below, and we&apos;ll send you a
          secure link to reset your password.
        </DialogContentText>

        <FormControl fullWidth>
          <OutlinedInput
            autoFocus
            required
            id="reset-email"
            name="email"
            placeholder="name@example.com"
            type="email"
            fullWidth
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={loading}
            sx={{
              borderRadius: 2,
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "black",
              }
            }}
          />
        </FormControl>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, pt: 1, gap: 1.5 }}>
        <Button
          onClick={handleClose}
          disabled={loading}
          variant="text"
          sx={{
            color: "text.secondary",
            textTransform: "none",
            fontWeight: 500
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          type="submit"
          disabled={loading}
          sx={{
            backgroundColor: "black",
            color: "white",
            textTransform: "none",
            fontWeight: 500,
            borderRadius: 2,
            px: 3,
            py: 1,
            "&:hover": {
              backgroundColor: "#222",
            }
          }}
        >
          {loading ? "Sending link..." : "Send Reset Link"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

ForgotPassword.propTypes = {
  handleClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
};

export default ForgotPassword;